/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useDismiss } from "./ui/useDismiss";
import { Check, ListFilter, Plus, X } from "lucide-react";
import { cn } from "../lib/utils";
import { DropdownPanel } from "./ui/DropdownPanel";
import { DateTimePicker } from "./ui/DateTimePicker";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import {
  filterValueLabel,
  toDateValue,
  toYmd,
  type ActiveFilter,
  type FilterField,
  type FilterFieldOption,
} from "../lib/filters";

const LOOKUP_DEBOUNCE_MS = 200;

/**
 * Linear-style filters. The toolbar trigger sits at the row's end at Button height; applied
 * filters wrap onto their own full-width row of chips with an add button and Clear filters.
 * One menu serves both triggers: pick a field, then a value (searchable list, a lookup that
 * searches records as you type, or a date-only picker). One chip per field; picking again
 * replaces it.
 */
export function FilterBar({
  fields,
  active,
  onChange,
  beside,
}: {
  fields: FilterField[];
  active: ActiveFilter[];
  onChange: (next: ActiveFilter[]) => void;
  /** Controls that sit with the Filter button at the toolbar's end, such as the view toggle. */
  beside?: ReactNode;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pickingField, setPickingField] = useState<FilterField | null>(null);
  const [search, setSearch] = useState("");
  const [draftDate, setDraftDate] = useState<Date | null>(null);
  const found = useLookupResults(pickingField, search);
  const [pickedNames, setPickedNames] = useState<Record<string, string>>({});
  const contentRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  anchorRef.current = anchor;

  const open = anchor !== null;

  const close = () => {
    setAnchor(null);
    setPickingField(null);
    setSearch("");
    setDraftDate(null);
  };

  useDismiss(open, close, [contentRef, anchorRef]);

  const toggle = (event: React.MouseEvent<HTMLElement>) => {
    if (open) {
      close();
    } else {
      setAnchor(event.currentTarget);
    }
  };

  const currentValue = (field: FilterField) =>
    active.find((filter) => filter.key === field.key)?.value ?? null;

  const beginField = (field: FilterField) => {
    const existing = currentValue(field);
    setPickingField(field);
    setSearch("");
    setDraftDate(field.kind === "date" && existing !== null ? toDateValue(existing) : null);
  };

  const beginEdit = (event: React.MouseEvent<HTMLElement>, field: FilterField) => {
    setAnchor(event.currentTarget);
    beginField(field);
  };

  const apply = (field: FilterField, option: FilterFieldOption) => {
    setPickedNames((names) => ({ ...names, [chipId(field, option.value)]: option.label }));
    onChange([
      ...active.filter((filter) => filter.key !== field.key),
      { key: field.key, value: option.value },
    ]);
    close();
  };

  return (
    <>
      <div className="flex items-center gap-2 self-end sm:ml-auto sm:self-auto">
        {beside}
        <Button variant="outline" size="md" aria-label="Filter" onClick={toggle}>
          <ListFilter size={15} className="mr-1.5" />
          Filter
        </Button>
      </div>
      {active.length > 0 && (
        <div className="flex w-full flex-wrap items-center gap-2">
          {active.map((filter) => {
            const field = fields.find((candidate) => candidate.key === filter.key);
            if (!field) {
              return null;
            }
            return (
              <span
                key={filter.key}
                className="glass inline-flex h-9 items-center gap-1.5 rounded-full py-1 pl-3 pr-1.5 text-xs text-text-secondary"
              >
                <button
                  type="button"
                  aria-label={`Edit ${field.label} filter`}
                  className="inline-flex items-center gap-1.5 rounded-full transition-colors hover:text-text-primary"
                  onClick={(event) => beginEdit(event, field)}
                >
                  <span className="text-text-tertiary">{field.label}</span>
                  <span>is</span>
                  <span className="font-medium text-text-primary">
                    <ChipValue
                      field={field}
                      value={filter.value}
                      picked={pickedNames[chipId(field, filter.value)] ?? null}
                    />
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${field.label} filter`}
                  className="grid size-5 place-items-center rounded-full text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary"
                  onClick={() => onChange(active.filter((other) => other.key !== filter.key))}
                >
                  <X size={12} />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1 rounded-full border border-dashed border-border px-3 text-xs text-text-tertiary transition-colors hover:border-border-hover hover:text-text-primary"
            onClick={toggle}
          >
            <Plus size={13} />
            Add filter
          </button>
          <span className="flex-1" />
          <Button variant="ghost" size="sm" onClick={() => onChange([])}>
            Clear filters
          </Button>
        </div>
      )}
      <DropdownPanel
        triggerRef={anchorRef}
        isOpen={open}
        maxHeight={340}
        className="z-40 flex flex-col overflow-hidden"
      >
        <div ref={contentRef} className="flex min-h-0 w-64 flex-col p-2">
          {pickingField === null ? (
            <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
              {fields.map((field) => (
                <li key={field.key}>
                  <button
                    type="button"
                    className="w-full rounded-lg px-3 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-hover hover:text-text-primary"
                    onClick={() => beginField(field)}
                  >
                    {field.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : pickingField.kind !== "date" ? (
            <>
              {(pickingField.kind === "lookup" || pickingField.searchable !== false) && (
                <Input
                  autoFocus
                  placeholder={pickingField.kind === "lookup" ? "Search…" : "Filter…"}
                  aria-label={`Filter ${pickingField.label} values`}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              )}
              <ul className="mt-2 min-h-0 flex-1 space-y-0.5 overflow-y-auto">
                {valueOptions(pickingField, search, found).map((option) => (
                    <li key={option.value}>
                      <button
                        type="button"
                        aria-pressed={option.value === currentValue(pickingField)}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-bg-hover hover:text-text-primary",
                          option.value === currentValue(pickingField)
                            ? "font-medium text-text-primary"
                            : "text-text-secondary",
                        )}
                        onClick={() => apply(pickingField, option)}
                      >
                        {option.label}
                        {option.value === currentValue(pickingField) && (
                          <Check size={14} className="shrink-0 text-interactive" />
                        )}
                      </button>
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            <div className="space-y-3 p-1">
              <DateTimePicker
                variant="date"
                editOnly
                datePlaceholder="MM/DD/YYYY"
                label={pickingField.label}
                dateValue={draftDate}
                onDateChange={setDraftDate}
              />
              <Button
                size="sm"
                disabled={draftDate === null}
                onClick={() => {
                  if (draftDate) {
                    const value = toYmd(draftDate);
                    apply(pickingField, { value, label: filterValueLabel(pickingField, value) });
                  }
                }}
              >
                Apply
              </Button>
            </div>
          )}
        </div>
      </DropdownPanel>
    </>
  );
}

function valueOptions(
  field: FilterField,
  search: string,
  found: FilterFieldOption[],
): FilterFieldOption[] {
  if (field.kind !== "choice") {
    return found;
  }
  const needle = search.trim().toLowerCase();
  return field.options.filter((option) => option.label.toLowerCase().includes(needle));
}

/** The records a lookup field finds for the typed search, debounced, latest query wins. */
function useLookupResults(field: FilterField | null, search: string): FilterFieldOption[] {
  const [found, setFound] = useState<FilterFieldOption[]>([]);
  const lookup = field?.kind === "lookup" ? field.lookup : undefined;
  useEffect(() => {
    if (!lookup) {
      setFound([]);
      return;
    }
    let current = true;
    const timer = setTimeout(() => {
      void lookup.search(search.trim()).then((options) => {
        if (current) {
          setFound(options);
        }
      });
    }, LOOKUP_DEBOUNCE_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [lookup, search]);
  return found;
}

function chipId(field: FilterField, value: string): string {
  return `${field.key}:${value}`;
}

/**
 * A chip's value: the label of the pick that made it, else (a filter restored from the url) a
 * lookup resolves its record's name and everything else labels at once.
 */
function ChipValue({
  field,
  value,
  picked,
}: {
  field: FilterField;
  value: string;
  picked: string | null;
}) {
  const [name, setName] = useState<string | null>(null);
  const lookup = field.kind === "lookup" && picked === null ? field.lookup : undefined;
  useEffect(() => {
    if (!lookup) {
      return;
    }
    let current = true;
    void lookup.resolve(value).then((resolved) => {
      if (current) {
        setName(resolved);
      }
    });
    return () => {
      current = false;
    };
  }, [lookup, value]);
  return <>{picked ?? name ?? filterValueLabel(field, value)}</>;
}
